/**
 * Curated role hub config for /jobs/role/{slug}, /tyopaikat/ammatti/{slug},
 * /lediga-jobb/yrke/{slug} pages (Aug 2026 search report, cluster 6).
 *
 * Deliberately only two healthcare roles: lähihoitaja työpaikat (4.1K searches/mo) and
 * sairaanhoitaja työpaikat (3.9K). Demand collapses after these (myyjä 1.0K, sähköasentaja
 * 800, …) — do not add role pages across the trades; thin role pages do more harm than good.
 *
 * `fieldValues` were read from live Typesense facet counts per collection (Aug 2026) and
 * include spelling/case variants. Re-check when the enrichment vocabulary drifts.
 */

import type { RoleDefinition, SupportedLocale } from '../../types/role-hub';

/** Path markers for the role segment per locale (en, fi, sv). */
export const ROLE_PATH_MARKERS = ['role', 'ammatti', 'yrke'] as const;

export function isRolePathMarker(segment: string): boolean {
  return (ROLE_PATH_MARKERS as readonly string[]).includes(segment.toLowerCase());
}

export const ROLE_HUB_CONFIG: RoleDefinition[] = [
  {
    slug: 'lahihoitaja',
    fieldValues: {
      fi: ['Lähihoitaja', 'Lähihoitajat', 'Kotihoidon Lähihoitaja'],
      en: ['Healthcare Assistant', 'Health Care Assistants', 'Nurse Assistant'],
      sv: [
        'Närvårdare',
        'Undersköterska',
        'Undersköterska, vård- och specialavdelning och mottagning',
        'Undersköterska, Vård- Och Specialavdelning Och Mottagning',
      ],
    },
    displayNames: {
      fi: 'Lähihoitaja',
      en: 'Practical Nurse',
      sv: 'Närvårdare',
    },
  },
  {
    slug: 'sairaanhoitaja',
    fieldValues: {
      fi: ['Sairaanhoitaja'],
      en: ['Nurse Responsible For General Care'],
      sv: ['Sjuksköterska, grundutbildad', 'Sjuksköterska, Grundutbildad'],
    },
    displayNames: {
      fi: 'Sairaanhoitaja',
      en: 'Registered Nurse',
      sv: 'Sjuksköterska',
    },
  },
];

export function getRoleBySlug(slug: string): RoleDefinition | null {
  if (!slug) return null;
  const normalized = slug.trim().toLowerCase();
  return ROLE_HUB_CONFIG.find((role) => role.slug === normalized) ?? null;
}

const ROLE_JOBS_SEGMENT: Record<SupportedLocale, string> = {
  en: 'jobs',
  fi: 'tyopaikat',
  sv: 'lediga-jobb',
};

const ROLE_PATH_MARKER_BY_LOCALE: Record<SupportedLocale, string> = {
  en: 'role',
  fi: 'ammatti',
  sv: 'yrke',
};

/** Builds the `/jobs/role/{slug}` (fi: ammatti, sv: yrke) path for a role hub in a given locale. */
export function buildRoleHubPath(slug: string, locale: SupportedLocale): string {
  return `/${locale}/${ROLE_JOBS_SEGMENT[locale]}/${ROLE_PATH_MARKER_BY_LOCALE[locale]}/${slug}`;
}

/**
 * True when `values` is exactly the role's field-value set for `locale`
 * (order- and case-insensitive). Used to keep the pretty /role/{slug} path and
 * omit redundant ?field= params when regenerating URLs from search state.
 */
export function fieldValuesMatchRole(
  role: RoleDefinition,
  locale: SupportedLocale,
  values: string[],
): boolean {
  const expected = new Set(role.fieldValues[locale].map((v) => v.toLowerCase()));
  const actual = new Set(values.map((v) => String(v).toLowerCase()));
  if (expected.size !== actual.size) return false;
  for (const v of actual) {
    if (!expected.has(v)) return false;
  }
  return true;
}
